import {
  IonAvatar,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonRow,
  IonText,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { RouteComponentProps, useLocation, withRouter } from "react-router";
import AsyncArtistButton from "../components/AsyncArtistButton";
import Content from "../components/Content";
import { useConfig } from "../data/store/useConfig";
import { useProfile } from "../data/store/useProfile";
import { useRoles } from "../data/store/useRoles";
import HostConfig from "../HostConfig";
import { LoginService } from "../services/LoginService";
import { APP_TITLE, ENABLE_MAGIC_LINK, iconUrl } from "../config/env";
import "./Login.scss";

interface OwnProps extends RouteComponentProps {}

interface LoginProps extends OwnProps, RouteComponentProps {}

type LoginFlowState =
  | "idle"           // enter email
  | "checking"       // looking up provider
  | "google-required" // existing Google account, must use Google
  | "sending"        // sending magic link
  | "sent"           // magic link sent, check inbox
  | "completing"     // completing sign-in from email link
  | "needs-email"    // magic link callback but no stored email (cross-device)
  | "error";

const Login: React.FC<LoginProps> = () => {
  const { search } = useLocation();
  const [target] = useState<string>(search.slice(2, search.length));
  const { authorId } = useProfile();
  const { role } = useRoles();
  const status = useProfile((x) => x.status);
  const authenticated = status === "authenticated";
  const isAlumni = useConfig((x) => x.isAlumni());

  const [email, setEmail] = useState<string>("");
  const [flowState, setFlowState] = useState<LoginFlowState>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Handle redirect when authenticated with a target
  useEffect(() => {
    if (authenticated && target.length > 3) {
      console.log(target);
      window.location.href = decodeURI(target);
    }
  });

  // Handle magic link callback (user clicked the link in their email)
  useEffect(() => {
    if (LoginService.isMagicLinkCallback()) {
      const storedEmail = window.localStorage.getItem('magicLinkEmail');
      if (storedEmail) {
        // Same device — complete automatically
        setFlowState("completing");
        LoginService.completeMagicLinkSignIn(storedEmail)
          .then(() => {
            setFlowState("idle");
          })
          .catch((err) => {
            console.error("Magic link sign-in error:", err);
            setErrorMessage(
              err.message || "Could not complete sign-in. Please try again."
            );
            setFlowState("error");
          });
      } else {
        // Cross-device — need user to confirm their email
        setFlowState("needs-email");
      }
    }
  }, []);

  const handleContinueWithEmail = () => {
    if (!email || !email.includes("@")) {
      setErrorMessage("Please enter a valid email address.");
      setFlowState("error");
      return;
    }
    setFlowState("checking");
    setErrorMessage("");

    LoginService.checkExistingProvider(email)
      .then((provider) => {
        if (provider === "google") {
          // Existing Google account — must use Google to preserve UID
          setFlowState("google-required");
        } else {
          // New user or existing magic link user — send magic link
          setFlowState("sending");
          LoginService.sendMagicLink(email)
            .then(() => setFlowState("sent"))
            .catch((err) => {
              console.error("Error sending magic link:", err);
              setErrorMessage(
                err.message || "Could not send sign-in email. Please try again."
              );
              setFlowState("error");
            });
        }
      })
      .catch((err) => {
        console.error("Error checking provider:", err);
        setErrorMessage(
          err.message || "Something went wrong. Please try again."
        );
        setFlowState("error");
      });
  };

  const handleCompleteWithEmail = () => {
    if (!email || !email.includes("@")) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    setFlowState("completing");
    setErrorMessage("");
    LoginService.completeMagicLinkSignIn(email)
      .then(() => {
        setFlowState("idle");
      })
      .catch((err) => {
        console.error("Magic link sign-in error:", err);
        setErrorMessage(
          err.message || "Could not complete sign-in. Please try again."
        );
        setFlowState("error");
      });
  };

  const handleGoogleSignIn = () => {
    LoginService.loginWithGoogle(email).catch((err) => {
      console.error("Google sign-in error:", err);
      setErrorMessage(
        err.message || "Google sign-in failed. Please try again."
      );
      setFlowState("error");
    });
  };

  return (
    <Content style={{ "--background": "#519f6b" }}>
      <IonCardHeader>
        {
          <IonAvatar style={{ width: 108, height: 108, margin: "auto" }}>
            <img
              src={iconUrl('apple-touch-icon.png')}
              alt={HostConfig.title}
            />
          </IonAvatar>
        }
      </IonCardHeader>
      <IonCardContent>
        <IonRow>
          <IonCol sizeMd="6" pushMd="3">
            {status === "unauthenticated" && (flowState === "idle" || flowState === "checking" || flowState === "error") && (
              <IonCard className="magic-link-card">
                <IonCardContent>
                  {ENABLE_MAGIC_LINK ? (
                    <>
                      <IonText>
                        <h2 style={{ textAlign: "center", marginBottom: 16 }}>
                          Sign in with email
                        </h2>
                        <p
                          style={{
                            textAlign: "center",
                            marginBottom: 16,
                            opacity: 0.8,
                          }}
                        >
                          Enter your email to get started — no password needed.
                        </p>
                      </IonText>
                      <IonItem>
                        <IonLabel position="floating">Email address</IonLabel>
                        <IonInput
                          aria-label="Email address"
                          type="email"
                          value={email}
                          onIonChange={(e) => setEmail(e.detail.value || "")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleContinueWithEmail();
                          }}
                          disabled={flowState === "checking"}
                        />
                      </IonItem>
                      {errorMessage && (
                        <IonText color="danger">
                          <p style={{ textAlign: "center", marginTop: 8 }}>
                            {errorMessage}
                          </p>
                        </IonText>
                      )}
                      <IonButton
                        color="primary"
                        expand="block"
                        onClick={handleContinueWithEmail}
                        disabled={flowState === "checking"}
                        style={{ marginTop: 16 }}
                      >
                        {flowState === "checking" ? "Checking..." : "Continue with email"}
                      </IonButton>
                      <div style={{ display: "flex", alignItems: "center", margin: "16px 0" }}>
                        <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.2)" }} />
                        <span style={{ padding: "0 12px", opacity: 0.6, fontSize: 14 }}>or</span>
                        <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.2)" }} />
                      </div>
                    </>
                  ) : (
                    <IonText>
                      <h2 style={{ textAlign: "center", marginBottom: 16 }}>
                        Sign in
                      </h2>
                    </IonText>
                  )}
                  {errorMessage && !ENABLE_MAGIC_LINK && (
                    <IonText color="danger">
                      <p style={{ textAlign: "center", marginTop: 8 }}>
                        {errorMessage}
                      </p>
                    </IonText>
                  )}
                  <IonButton
                    expand="block"
                    fill={ENABLE_MAGIC_LINK ? "outline" : "solid"}
                    color="primary"
                    onClick={() => LoginService.loginWithGoogle().catch((err) => {
                      console.error("Google sign-in error:", err);
                      setErrorMessage(err.message || "Google sign-in failed. Please try again.");
                      setFlowState("error");
                    })}
                    disabled={flowState === "checking"}
                  >
                    Sign in with Google
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}

            {status === "unauthenticated" && flowState === "needs-email" && (
              <IonCard className="magic-link-card">
                <IonCardContent>
                  <IonText>
                    <h2 style={{ textAlign: "center", marginBottom: 16 }}>
                      Confirm your email
                    </h2>
                    <p
                      style={{
                        textAlign: "center",
                        marginBottom: 16,
                        opacity: 0.8,
                      }}
                    >
                      It looks like you opened the sign-in link on a different
                      device. Please enter your email to complete sign-in.
                    </p>
                  </IonText>
                  <IonItem>
                    <IonLabel position="floating">Email address</IonLabel>
                    <IonInput
                      aria-label="Email address"
                      type="email"
                      value={email}
                      onIonChange={(e) => setEmail(e.detail.value || "")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCompleteWithEmail();
                      }}
                    />
                  </IonItem>
                  {errorMessage && (
                    <IonText color="danger">
                      <p style={{ textAlign: "center", marginTop: 8 }}>
                        {errorMessage}
                      </p>
                    </IonText>
                  )}
                  <IonButton
                    color="primary"
                    expand="block"
                    onClick={handleCompleteWithEmail}
                    style={{ marginTop: 16 }}
                  >
                    Complete Sign-In
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}

            {status === "unauthenticated" && flowState === "google-required" && (
              <IonCard className="magic-link-card">
                <IonCardContent>
                  <IonText>
                    <h2 style={{ textAlign: "center", marginBottom: 16 }}>
                      Welcome back
                    </h2>
                    <p style={{ textAlign: "center", marginBottom: 16 }}>
                      <strong>{email}</strong> is linked to a Google account.
                      Please sign in with Google to continue.
                    </p>
                  </IonText>
                  <IonButton
                    color="primary"
                    expand="block"
                    onClick={handleGoogleSignIn}
                  >
                    Sign in with Google
                  </IonButton>
                  <IonButton
                    fill="outline"
                    expand="block"
                    onClick={() => {
                      setFlowState("idle");
                      setEmail("");
                      setErrorMessage("");
                    }}
                    style={{ marginTop: 8 }}
                  >
                    Use a different email
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}

            {status === "unauthenticated" && flowState === "sent" && (
              <IonCard className="magic-link-card">
                <IonCardContent>
                  <IonText>
                    <h2 style={{ textAlign: "center", marginBottom: 16 }}>
                      Check your inbox
                    </h2>
                    <p style={{ textAlign: "center", marginBottom: 8 }}>
                      We sent a sign-in link to <strong>{email}</strong>
                    </p>
                    <p
                      style={{
                        textAlign: "center",
                        marginBottom: 16,
                        opacity: 0.8,
                      }}
                    >
                      Click the link in the email to sign in. You can close this
                      tab.
                    </p>
                  </IonText>
                  <IonButton
                    fill="outline"
                    expand="block"
                    onClick={() => {
                      setFlowState("idle");
                      setEmail("");
                      setErrorMessage("");
                    }}
                  >
                    Use a different email
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}

            <IonLoading
              isOpen={
                status === "unknown" || flowState === "completing" || flowState === "sending"
              }
              message={
                flowState === "completing"
                  ? "Completing sign-in..."
                  : flowState === "sending"
                  ? "Sending sign-in link..."
                  : undefined
              }
            />
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol sizeMd="6" pushMd="3">
            {authenticated && (
              <IonCard>
                <IonCardHeader>
                  Welcome to {APP_TITLE}{" "}
                  {authorId && <AsyncArtistButton id={authorId} />}
                </IonCardHeader>

                <IonCardContent>
                  {isAlumni && (
                    <IonButton
                      fill="outline"
                      routerLink="/conversations?thread=start"
                    >
                      Conversations
                    </IonButton>
                  )}

                  {isAlumni && (
                    <IonButton fill="outline" routerLink="/sessions">
                      Sessions
                    </IonButton>
                  )}

                  {isAlumni && (
                    <IonButton fill="outline" routerLink="/transmit">
                      Transmit
                    </IonButton>
                  )}

                  <IonButton fill="outline" routerLink="/account">
                    Account
                  </IonButton>

                  <IonButton fill="outline" routerLink="/songs">
                    Songs
                  </IonButton>

                  {role && role.authority && role.authority.includes("admin") && (
                    <IonButton fill="outline" routerLink="/admin">
                      Admin
                    </IonButton>
                  )}
                </IonCardContent>
              </IonCard>
            )}
          </IonCol>
        </IonRow>
      </IonCardContent>
    </Content>
  );
};

export default withRouter(Login);
